import React, { Fragment, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useDispatch } from "react-redux";
import classnames from "classnames";
import _ from "lodash";

import { useIntlTranslate } from "../../../../../../hooks";
import { Mobile, NotMobile } from "../../../../../../hocs/Responsive";
import { Icon } from "../../../../../../components/icon/Icon";
import { Input } from "../../../../../../components/input/Input";
import { isCodeSymbolsValid } from "../../../../../../helpers/validation.helper";
import { checkCode } from "../../../../../../store/devices/requestsWithCode";
import { SBLogicalDevice } from "../../../../../../store/devices/devices.types";

import "./CheckCodeField.scss";
import { FormattedProfileName } from "../../../../../../components/formattedProfileName/FormattedProfileName";

const SEARCH_TIME_LIMIT = 60 * 1000;
const SEARCHING_IDLE = 5 * 1000;
const SEARCH_TIME_SECONDS = SEARCH_TIME_LIMIT / 1000;

const clearIntervalIfSet = (intervalId: NodeJS.Timeout | null) =>
  intervalId && clearInterval(intervalId);
const clearTimeoutIfSet = (timeoutId: NodeJS.Timeout | null) =>
  timeoutId && clearTimeout(timeoutId);

const normalize = (value: string) => value.trim().toLocaleLowerCase();

interface CheckCodeFieldProps {
  allDevices: Record<string, SBLogicalDevice>;
  onDeviceSearchNotFound: () => void;
  onDeviceSearchSuccess: (address: string) => void;
  onDeviceSearchProgress: () => void;
}
interface CheckCodeFieldInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  hasError: boolean;
}
interface ProgressInfoProps {
  timeRemaining: number;
}
interface SuccessInfoProps {
  address: string;
}

export const CheckCodeField = ({
  allDevices,
  onDeviceSearchNotFound,
  onDeviceSearchSuccess,
  onDeviceSearchProgress,
}: CheckCodeFieldProps) => {
  const dispatch = useDispatch();

  const [code, setCode] = useState<string>("");
  const [hasError, setHasError] = useState<boolean>(false);
  const [searching, setSearching] = useState<boolean>(false);
  const [existingDevice, setExistingDevice] = useState<Object>(null);
  const [macFound, setMacFound] = useState<boolean>(false);
  const [macNotFound, setMacNotFound] = useState<boolean>(false);
  const [address, setAddress] = useState<string>("");
  const [timeRemaining, setTimeRemaining] = useState<number>(0);

  const remainingTimeTick = useRef<NodeJS.Timeout | null>(null);
  const delaySearchTimeout = useRef<NodeJS.Timeout | null>(null);
  const searchStarted = useRef<number>(0);

  useEffect(() => {
    return () => {
      clearIntervalIfSet(remainingTimeTick.current);
      clearTimeoutIfSet(delaySearchTimeout.current);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const code = e.target.value;
    const isValid = isCodeSymbolsValid(code);
    const isFull = code.length === 4;

    onDeviceSearchNotFound();

    setSearching(false);
    setExistingDevice(null);
    setMacFound(false);
    setMacNotFound(false);

    setCode(code);
    setHasError(!code || !isValid);

    if (isFull && isValid) {
      startSearch(code);
    }
  };

  const startSearch = (code: string): void => {
    setSearching(true);
    onDeviceSearchProgress();

    searchStarted.current = Date.now();
    clearTimeoutIfSet(delaySearchTimeout.current);

    findDevice(code);
  };

  const findDevice = (code: string): void => {
    const tail = normalize(code);

    const check = (code: string): Promise<string> => {
      return new Promise((resolve, reject) => {
        dispatch(checkCode(code), { showLoader: false })
          .then((address) => {
            resolve(address);
          })
          .catch((error) => {
            reject(error);
          });
      });
    };

    check(tail).then((address: string) => {
      if (!address) {
        delaySearch(tail);
        return;
      }

      const savedDevice = _.find(allDevices, ({ identifiers }) =>
        identifiers.includes(address)
      );

      if (savedDevice) {
        setExistingDevice(savedDevice);
      } else {
        setAddress(address);
        onDeviceSearchSuccess(address);
      }

      setMacFound(true);
      setMacNotFound(false);
      setSearching(false);
      setTimeRemaining(0);
      clearIntervalIfSet(remainingTimeTick.current);
    });
  };

  const delaySearch = (code: string): void => {
    const timeRemaining =
      searchStarted.current + SEARCH_TIME_LIMIT - Date.now();

    if (timeRemaining > 0) {
      setRemainingTime(timeRemaining);
      delaySearchTimeout.current = setTimeout(() => {
        findDevice(code);
        clearTimeoutIfSet(delaySearchTimeout.current);
      }, SEARCHING_IDLE);
    } else {
      setSearching(false);
      setMacFound(false);
      setMacNotFound(true);
      onDeviceSearchNotFound();
    }
  };

  const setRemainingTime = (timeRemaining: number): void => {
    const timeout = 100;

    clearIntervalIfSet(remainingTimeTick.current);
    remainingTimeTick.current = setInterval(() => {
      setTimeRemaining(parseInt((timeRemaining / 1000).toFixed(0)));
      timeRemaining -= timeout;
      if (timeRemaining < 0) {
        clearIntervalIfSet(remainingTimeTick.current);
      }
    }, timeout);
  };

  const renderInfoPanel = () => {
    if (searching) return <ProgressInfo timeRemaining={timeRemaining} />;
    if (macFound && !existingDevice) return <SuccessInfo address={address} />;
    if (macFound && existingDevice)
      return <ExistingDeviceInfo device={existingDevice} />;
    if (macNotFound) return <FailureInfo />;
    return <DefaultInfo />;
  };

  const fieldClasses = {
    "check-code-field__input": true,
    "check-code-field__input--searching": searching,
  };

  return (
    <div className={"check-code-field"}>
      <div className={classnames(fieldClasses)}>
        <CheckCodeFieldInput
          value={code}
          hasError={hasError}
          onChange={handleInputChange}
          aria-labelledby="check_code_field_hint"
        />
        <Icon icon="fa-solid fa-spinner fa-pulse fa-2x color-primary" />
      </div>
      {!hasError && (
        <div className="check-code-field__hint">
          <Icon icon="fa-regular fa-info-circle" />
          <div id="check_code_field_hint">
            <FormattedMessage id={"check_code_field_hint"} />
          </div>
        </div>
      )}
      <div className="check-code-field__info">{renderInfoPanel()}</div>
    </div>
  );
};

const CheckCodeFieldInput = ({
  value,
  hasError,
  onChange,
}: CheckCodeFieldInputProps) => {
  const translate = useIntlTranslate();

  const error =
    hasError &&
    (value
      ? translate("check_code_field_error")
      : translate("transaction_required"));

  const inputProps = {
    id: "device-code",
    label: <FormattedMessage id={"label_code"} />,
    placeholder: translate("four_character_code_placeholder"),
    "aria-labelledby": "check_code_field_hint",
    maxLength: 4,
    error,
    value,
    onChange,
  };

  return (
    <Fragment>
      <Mobile>
        <Input {...inputProps} />
      </Mobile>
      <NotMobile>
        <Input {...inputProps} autoFocus />
      </NotMobile>
    </Fragment>
  );
};

const ProgressInfo = ({ timeRemaining }: ProgressInfoProps) => {
  const translate = useIntlTranslate();
  const searchingStatusMessage = translate("aria_search_in_progress_wait", {
    searchingTime: SEARCH_TIME_SECONDS,
  });

  return (
    <div
      className={"info-progress"}
      aria-label={searchingStatusMessage}
      role="status"
      aria-live="polite"
    >
      <div aria-hidden={true}>
        <FormattedMessage id={"search_in_progress_wait"} />
        ... {timeRemaining}
      </div>
    </div>
  );
};

const SuccessInfo = ({ address }: SuccessInfoProps) => {
  return (
    <div className={"info-found"}>
      <div className={"address"}>{address}</div>
      <div className={"message"}>
        <Icon icon={"fa-regular fa-check"} />
        <span role="status" aria-live="polite">
          <FormattedMessage id={"device_found"} />
        </span>
      </div>
    </div>
  );
};

const FailureInfo = () => {
  return (
    <div className={"info-not-found"} role="status" aria-live="polite">
      <FormattedMessage id={"no_device_found"} />
    </div>
  );
};

const DefaultInfo = () => {
  return (
    <div className="info-default">
      <FormattedMessage id={"discovered_device_will_appear_here"} />
    </div>
  );
};

const ExistingDeviceInfo = ({ device }) => {
  return (
    <div className="info-existing">
      <div>
        <FormattedMessage id={"discovered_device_is_saved"} />
      </div>
      <div>
        <span className={"info-existing__label"}>
          <FormattedMessage id={"name"} />:
        </span>
        <span className={"info-existing__value"}>{device.name}</span>
      </div>
      <div>
        <span className={"info-existing__label"}>
          <FormattedMessage id={"group"} />:
        </span>
        <span className={"info-existing__value"}>
          <FormattedProfileName profileName={device.profile} />
        </span>
      </div>
    </div>
  );
};
